import React, { useEffect, useState } from 'react';
import { StudyGuideBlockNav } from './study-guide-block-nav';
import { StudyGuideBlockArticle } from './study-guide-block-article';
import { FiVideo } from 'react-icons/fi';
import { StudyGuideBlockModal } from './study-guide-block-modal';
import { useClient } from 'jsonapi-react';
import { useParams } from 'react-router-dom';
import { Loader } from '@/components/custom/loader';
import { EmptyState } from '@/components/custom/empty-state';
import { DashboardContent } from '@/layouts/dashboard';
import { BadgeTruncate } from '@/components/ui/badge';
import { Dialog, DialogTrigger } from '@/components/ui/dialog';
import { GuideBlock, GuideBlockBtn, GuideBlockContent, GuideBlockHeader, GuideBlockHeaderContent, GuideBlockTitle } from '@/components/custom/guide';

export function StudyGuideBlock() {
  const [finished, setFinished] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [video, setVideo] = useState(null);
  const [scormData, setScormData] = useState(null);
  const [sections, setSections] = useState([]);
  const [activeSectionIndex, setActiveSectionIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const { blockId } = useParams();

  const client = useClient();

  const fetchData = () => {
    setLoading(true);
    Promise.all([getStudyPlanVideoById(blockId), getScormData(blockId)]).then(([video, scorm]) => {
      const sortedScorm = sortScormDataByScormId(scorm);

      const questionsScorm = sortedScorm.filter(item => item.scorm.scorm_type === 'questions');
      const otherScorm = sortedScorm.filter(item => item.scorm.scorm_type !== 'questions');

      const sections = [...otherScorm, ...questionsScorm].map(section => ({
        id: section.id,
        title: section.scorm.scorm_type === 'introduction' ? 'Introdução' : section.scorm.scorm_type === 'questions' ? 'Avaliação de conhecimento' : section.scorm.title,
        done: section.done,
        isActive: false
      }));

      setSections(sections);
      setVideo(video);
      setScormData([...otherScorm, ...questionsScorm]);
      getIndexOfActiveSession([...otherScorm, ...questionsScorm]);
    });
    setLoading(false);
  };

  const getStudyPlanVideoById = async id => {
    const response = await client.fetch(`study_plan_videos/${id}`);
    return response.data;
  };

  const getScormData = async id => {
    const response = await client.fetch(`study_plan_videos/${id}/scorm_contents`);
    return response.data;
  };

  const getIndexOfActiveSession = scorm => {
    const index = scorm.findIndex(s => !s.done);
    if (index === -1) setActiveSectionIndex(0);
    else setActiveSectionIndex(index);

    const newScorm = scorm.map((s, i) => (i === activeSectionIndex ? { ...s, isActive: true } : { ...s, isActive: false }));
    setScormData(newScorm);
  };

  const sortScormDataByScormId = scorm => {
    return scorm.sort((a, b) => a.scorm.id - b.scorm.id);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const breadcrumbs = taxonomy => ({
    title: 'Aprendizado Dinâmico',
    nav: [
      {
        route: '/guia-de-estudos',
        name: 'Guia de estudos',
        isActive: false
      },
      {
        route: `/guia-de-estudos/bloco-de-estudo/${blockId}`,
        name: taxonomy,
        isActive: true
      }
    ]
  });

  return (
    <DashboardContent breadcrumbs={breadcrumbs(video?.taxonomy?.name)}>
      {loading && <Loader />}

      {video && !loading && (
        <GuideBlock>
          <GuideBlockHeader>
            <GuideBlockHeaderContent>
              <GuideBlockTitle>{video?.taxonomy?.name}</GuideBlockTitle>

              <BadgeTruncate
                variant="primary-light"
                items={video.video?.taxonomies}
                oneLine
              />
            </GuideBlockHeaderContent>

            <Dialog>
              <DialogTrigger asChild>
                <GuideBlockBtn
                  type="button"
                  onClick={() => setShowModal(true)}
                >
                  <FiVideo /> Conteúdo em vídeo
                </GuideBlockBtn>
              </DialogTrigger>

              <StudyGuideBlockModal
                show={showModal}
                onClose={() => setShowModal(false)}
                video={video.video}
              />
            </Dialog>
          </GuideBlockHeader>

          <GuideBlockContent>
            {scormData && scormData.length > 0 ? (
              <>
                <StudyGuideBlockNav
                  data={sections}
                  finished={finished}
                  setActiveSectionIndex={setActiveSectionIndex}
                  activeSectionIndex={activeSectionIndex}
                />

                <StudyGuideBlockArticle
                  scorm={scormData[activeSectionIndex]}
                  study_plan_video_id={blockId}
                  introduction_image={video.video['introduction-image']}
                  last={activeSectionIndex === scormData.length - 1}
                  cta={() => setShowModal(true)}
                  allSectionsDone={scormData.findIndex(s => !s.done) === -1}
                  allSections={scormData}
                  setActiveSectionIndex={setActiveSectionIndex}
                  activeSectionIndex={activeSectionIndex}
                  setSections={setSections}
                />
              </>
            ) : (
              <EmptyState
                type="data"
                title="Seu conteúdo ainda está sendo gerado"
                text="A nossa inteligência artificial está preparando o conteúdo para você. Aguarde um momento e tente novamente."
                cta={{ text: 'Assista a aula disponível', onClick: () => setShowModal(true) }}
              />
            )}
          </GuideBlockContent>
        </GuideBlock>
      )}
    </DashboardContent>
  );
}
